/**
 * The hero navigation: deep-blue controls sitting directly on the bright hero
 * with no container of their own until the page scrolls.
 *
 * It is part of the composition rather than a component placed over it, which
 * is why readability comes from the crest's scrim keeping the top of the hero
 * near-black rather than from the nav carrying its own surface. A full-bleed
 * light sky backdrop fades in once the page has scrolled, for the moment the
 * artwork rises behind the type — deliberately edge to edge, and square at the top:
 * the instant it gains a radius there it reads as a floating card again.
 *
 * **There are no navigation links.** The design package's kit ships "How it
 * works" and "About", and both are sections that do not exist on this page. A
 * link that scrolls nowhere is worse than an absent one, so they are omitted.
 * The mobile menu is reserved for the real utility actions that cannot fit in
 * the tight header: account access, language, and the waitlist CTA.
 *
 * Mounted as a sibling *before* the hero, never inside it: the hero needs
 * `overflow: hidden` to clip the crest, and an ancestor with that becomes the
 * sticky scrollport — which never scrolls internally, so the sticky range is
 * zero and the nav silently behaves as `static`. In page flow it pins to the
 * document, and a negative margin pulls the hero back up underneath so the two
 * still read as one composition.
 */

import { useEffect, useState } from 'react';
import { Languages, Menu, X } from 'lucide-react';

import { useLanguage } from '../i18n/language.js';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import { LandingWordmark } from './LandingWordmark.js';

const HEIGHT = 56;
const NAV_MENU_RADIUS = 'var(--radius-sm)';
const NAV_ACTION_RADIUS = 'var(--radius-2xs)';

export function HeroNavigation({
  onHome,
  onHowItWorks,
  secondaryAction,
}: {
  readonly onHome: () => void;
  readonly onHowItWorks: () => void;
  /**
   * The single account destination — "Sign in", "Dashboard", or "Business
   * profile". A slot rather than a built-in keeps this component free of
   * session knowledge.
   */
  readonly secondaryAction?: {
    readonly label: string;
    readonly onSelect: () => void;
  };
}): React.ReactElement {
  const { copy, language, toggle } = useLanguage();
  const compact = useMediaQuery('(max-width: 860px)');
  const tight = useMediaQuery('(max-width: 560px)');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Which of the page's screens is in view, for the indicator on the right.
  const [screen, setScreen] = useState(0);
  const [screenCount, setScreenCount] = useState(0);
  useEffect(() => {
    const screens = Array.from(
      document.querySelectorAll<HTMLElement>('[data-landing-screen]'),
    );
    setScreenCount(screens.length);
    if (screens.length === 0 || typeof IntersectionObserver === 'undefined')
      return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setScreen(screens.indexOf(entry.target as HTMLElement));
          }
        }
      },
      { threshold: 0.5 },
    );
    screens.forEach((element) => {
      observer.observe(element);
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  const goToScreen = (index: number): void => {
    const screens = document.querySelectorAll<HTMLElement>('[data-landing-screen]');
    screens[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const onScroll = (): void => {
      setScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!tight) setMenuOpen(false);
  }, [tight]);

  const quiet = {
    font: 'var(--type-body-md)',
    color: 'rgba(10,36,54,.78)',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'color var(--duration-fast) var(--ease-standard)',
  };
  const showMenuButton = tight;
  const menuItem = {
    width: '100%',
    minHeight: 44,
    padding: '10px 12px',
    border: 0,
    borderRadius: NAV_ACTION_RADIUS,
    background: 'transparent',
    color: 'var(--text-primary)',
    font: 'var(--type-button-sm)',
    textAlign: 'start' as const,
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        width: '100%',
        marginBottom: -HEIGHT,
      }}
    >
      <span
        data-testid="landing-nav-backdrop"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(239,249,253,0.84)',
          backdropFilter: 'saturate(130%) blur(14px)',
          WebkitBackdropFilter: 'saturate(130%) blur(14px)',
          borderBottom: '1px solid rgba(10,36,54,0.08)',
          // Bottom corners only — a soft shelf hanging from the top of the
          // viewport, not a detached capsule.
          borderRadius: `0 0 ${NAV_MENU_RADIUS} ${NAV_MENU_RADIUS}`,
          opacity: scrolled ? 1 : 0,
          transition: 'opacity var(--duration-slow) var(--ease-standard)',
          pointerEvents: 'none',
        }}
      />
      <nav
        className="mj-landing-nav"
        aria-label={copy.nav.home}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: compact ? 12 : 20,
          width: '100%',
          maxWidth: 1240,
          minHeight: HEIGHT,
          margin: '0 auto',
          padding: compact ? '0 12px' : '0 32px',
        }}
      >
        <button
          type="button"
          onClick={onHome}
          aria-label={copy.nav.home}
          style={{ ...quiet, display: 'inline-flex' }}
        >
          <LandingWordmark height={compact ? 26 : 30} />
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 8 : 24,
          }}
        >
          {!tight && screenCount > 1 ? (
            <span
              className="mj-landing-screens"
              role="group"
              aria-label={copy.nav.sections}
            >
              {Array.from({ length: screenCount }, (_, index) => (
                <button
                  key={String(index)}
                  type="button"
                  aria-label={copy.nav.section(index + 1, screenCount)}
                  aria-current={index === screen ? 'true' : undefined}
                  onClick={() => {
                    goToScreen(index);
                  }}
                />
              ))}
              {/* The counter is a fraction, so it keeps its own direction and
                  reads "2 / 4" in Arabic too. */}
              <b aria-hidden="true" dir="ltr">
                {screen + 1} / {screenCount}
              </b>
            </span>
          ) : null}
          {!tight ? (
            <button
              type="button"
              className="mj-landing-expanded-only"
              onClick={onHowItWorks}
              style={{ ...quiet, minHeight: 44, padding: '0 4px' }}
            >
              {copy.nav.howItWorks}
            </button>
          ) : null}

          {!tight ? (
            <button
              type="button"
              onClick={toggle}
              // The visible text is the *other* language's name, which is the
              // convention every bilingual Saudi site uses. The accessible name
              // says what pressing it does, because "العربية" alone does not.
              aria-label={copy.nav.switchLanguage}
              lang={language === 'en' ? 'ar' : 'en'}
              style={{
                ...quiet,
                fontFamily:
                  language === 'en' ? 'var(--font-arabic)' : 'var(--font-sans)',
                // A 44px target without a 44px box: the label is small, the
                // touchable area is not.
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '0 4px',
              }}
            >
              <Languages size={20} strokeWidth={1.5} aria-hidden="true" />
              {copy.nav.otherLanguageName}
            </button>
          ) : null}

          {secondaryAction && !tight ? (
            <button
              type="button"
              onClick={secondaryAction.onSelect}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 44,
                padding: '10px 20px',
                borderRadius: NAV_ACTION_RADIUS,
                border: '1px solid transparent',
                background: '#16191B',
                color: '#FFFFFF',
                font: 'var(--type-button-sm)',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.16)',
                transition: 'var(--transition-control)',
              }}
            >
              {secondaryAction.label}
            </button>
          ) : null}

          {showMenuButton ? (
            <button
              type="button"
              onClick={() => {
                setMenuOpen((open) => !open);
              }}
              aria-label={menuOpen ? copy.nav.closeMenu : copy.nav.openMenu}
              aria-expanded={menuOpen}
              aria-controls="landing-mobile-menu"
              style={{
                ...quiet,
                display: 'inline-flex',
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              {menuOpen ? (
                <X size={20} strokeWidth={1.5} aria-hidden="true" />
              ) : (
                <Menu size={20} strokeWidth={1.5} aria-hidden="true" />
              )}
            </button>
          ) : null}
        </div>

        {showMenuButton && menuOpen ? (
          <div
            id="landing-mobile-menu"
            style={{
              position: 'absolute',
              top: HEIGHT - 4,
              insetInlineEnd: 12,
              minWidth: 180,
              padding: 8,
              border: '1px solid rgba(10,36,54,.12)',
              borderRadius: NAV_MENU_RADIUS,
              background: 'rgba(251,253,252,.98)',
              boxShadow: 'var(--elevation-2)',
            }}
          >
            {secondaryAction ? (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  secondaryAction.onSelect();
                }}
                style={{
                  ...menuItem,
                  background: '#16191B',
                  color: '#FFFFFF',
                  textAlign: 'center',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.16)',
                }}
              >
                {secondaryAction.label}
              </button>
            ) : null}

            <button
              type="button"
              className="mj-landing-expanded-only"
              onClick={() => {
                setMenuOpen(false);
                onHowItWorks();
              }}
              style={menuItem}
            >
              {copy.nav.howItWorks}
            </button>

            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                toggle();
              }}
              aria-label={copy.nav.switchLanguage}
              lang={language === 'en' ? 'ar' : 'en'}
              style={{
                ...menuItem,
                fontFamily:
                  language === 'en' ? 'var(--font-arabic)' : 'var(--font-sans)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                textAlign: 'center',
              }}
            >
              <Languages size={20} strokeWidth={1.5} aria-hidden="true" />
              {copy.nav.otherLanguageName}
            </button>
          </div>
        ) : null}
      </nav>
    </div>
  );
}
